import {
  getBackendAccessTokenExpiresAt,
  isSessionExpiredStatus,
} from '~/utils/authSessionExpiry';
import { reportClientApiNetworkFailure } from '~/utils/telegramErrorMonitor';

const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;
const FETCH_PATCH_FLAG = '__cleverDocsAuthFetchPatched';

export default defineNuxtPlugin(() => {
  const { data: authSession, status } = useAuth();
  let timeoutId: ReturnType<typeof window.setTimeout> | null = null;

  patchWindowFetch();

  const clearTimer = () => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const scheduleLogout = () => {
    clearTimer();

    if (status.value !== 'authenticated') {
      return;
    }

    const expiresAt = getBackendAccessTokenExpiresAt(authSession.value);

    if (!expiresAt) {
      return;
    }

    const delay = expiresAt - Date.now();

    if (delay <= 0) {
      void useLogoutFromAuth();
      return;
    }

    timeoutId = window.setTimeout(
      scheduleLogout,
      Math.min(delay, MAX_TIMEOUT_DELAY_MS),
    );
  };

  watch([() => status.value, () => authSession.value], scheduleLogout, {
    immediate: true,
  });

  const handleVisibleAgain = () => {
    if (!document.hidden) {
      scheduleLogout();
    }
  };

  window.addEventListener('focus', scheduleLogout);
  document.addEventListener('visibilitychange', handleVisibleAgain);
});

const patchWindowFetch = () => {
  const windowWithPatchFlag = window as typeof window & {
    [FETCH_PATCH_FLAG]?: boolean;
  };

  if (windowWithPatchFlag[FETCH_PATCH_FLAG]) {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  windowWithPatchFlag[FETCH_PATCH_FLAG] = true;

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);

    if (isInternalApiRequest(input)) {
      if (isSessionExpiredStatus(response.status)) {
        void useLogoutFromAuth();
      }

      if (response.status >= 500) {
        void reportInternalApiResponseFailure(input, init, response);
      }
    }

    return response;
  };
};

const isInternalApiRequest = (input: RequestInfo | URL) => {
  const rawUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;

  try {
    const url = new URL(rawUrl, window.location.origin);

    return (
      url.origin === window.location.origin && url.pathname.startsWith('/api/')
    );
  } catch {
    return false;
  }
};

const internalApiPath = (input: RequestInfo | URL) => {
  const rawUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;

  try {
    const url = new URL(rawUrl, window.location.origin);

    return `${url.pathname}${url.search}`;
  } catch {
    return rawUrl;
  }
};

const requestMethod = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.method) {
    return init.method;
  }

  if (typeof input !== 'string' && !(input instanceof URL) && input.method) {
    return input.method;
  }

  return 'GET';
};

const readMonitoringResponseBody = async (
  response: Response,
): Promise<unknown> => {
  try {
    const clone = response.clone();
    const contentType = clone.headers.get('content-type') ?? '';

    if (contentType.includes('application/json')) {
      return await clone.json();
    }

    const text = await clone.text();

    return text ? text.slice(0, 2000) : null;
  } catch {
    return null;
  }
};

const reportInternalApiResponseFailure = async (
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  response: Response,
) => {
  const responseBody = await readMonitoringResponseBody(response);

  await reportClientApiNetworkFailure(
    internalApiPath(input),
    {
      response: {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        data: responseBody,
      },
    },
    {
      source: 'window.fetch',
      method: requestMethod(input, init).toUpperCase(),
    },
  );
};
